import type { StufenBeschreibung } from '@luzula/luzula-lib';

/**
 * Torf-Zersetzungsgrad nach von Post (H1–H10), SEA Teil A, Darst. A 2-3, S. 92.
 *
 * Der Eingabewert in der Substratfolge ist die Ziffer (1 … 10). Die Reihe
 * liegt hier und nicht im Hilfefenster, weil zwei Stellen sie brauchen: die
 * Tabelle im Fenster und das Stufen-Feld in der Substratfolge (Farbe und Name
 * je Wert). Eine Reihe, nicht zwei.
 */
export type ZersetzungsGruppe = 'ge' | 'mä' | 'st';

export interface ZersetzungsgradStufe {
	/** Eingabewert (1 … 10). */
	wert: number;
	/** Kurzzeichen nach von Post. */
	code: string;
	bezeichnung: string;
	pflanzenstruktur: string;
	quetschen: string;
	pflanzenreste: string;
	/** Gruppe der Anweisung: gering / mäßig / stark zersetzt. */
	gruppe: ZersetzungsGruppe;
}

/** Name und Farbe je Gruppe — die Legende des Hilfefensters. */
export const ZERSETZUNGSGRAD_GRUPPEN: Readonly<Record<ZersetzungsGruppe, { name: string; farbe: string }>> = {
	'ge': { name: 'gering', farbe: '#d4edda' },
	'mä': { name: 'mäßig', farbe: '#fff3cd' },
	'st': { name: 'stark', farbe: '#f8d7da' },
};

export const ZERSETZUNGSGRAD_STUFEN: ZersetzungsgradStufe[] = [
	{ wert: 1, code: 'H1', bezeichnung: 'nicht zersetzt', pflanzenstruktur: 'unverändert, völlig erhalten', quetschen: 'klares, farbloses Wasser; kein Torf tritt durch', pflanzenreste: 'nicht breiig, nicht verändert', gruppe: 'ge' },
	{ wert: 2, code: 'H2', bezeichnung: 'fast nicht zersetzt', pflanzenstruktur: 'fast unverändert, deutlich erkennbar', quetschen: 'fast klares, gelbbraunes Wasser; kein Torf tritt durch', pflanzenreste: 'nicht breiig, kaum verändert', gruppe: 'ge' },
	{ wert: 3, code: 'H3', bezeichnung: 'sehr schwach zersetzt', pflanzenstruktur: 'noch gut erkennbar, einzelne Reste leicht breiig', quetschen: 'deutlich trübes Wasser; kein Torf tritt durch', pflanzenreste: 'nicht breiig, wenig verändert', gruppe: 'ge' },
	{ wert: 4, code: 'H4', bezeichnung: 'schwach zersetzt', pflanzenstruktur: 'undeutlich, zum Teil zerfallen', quetschen: 'stark trübes Wasser; kein Torf tritt durch', pflanzenreste: 'etwas breiig, zerbrochen', gruppe: 'ge' },
	{ wert: 5, code: 'H5', bezeichnung: 'mittel zersetzt', pflanzenstruktur: 'nur noch teilweise erkennbar', quetschen: 'stark trübes Wasser; wenig Torf tritt durch', pflanzenreste: 'stark breiig, erkennbare Reste und Fasern', gruppe: 'mä' },
	{ wert: 6, code: 'H6', bezeichnung: 'stark zersetzt', pflanzenstruktur: 'undeutlich', quetschen: 'trübe, breiige Masse; < ⅓ tritt durch', pflanzenreste: 'stark breiig, wenig erkennbare Reste', gruppe: 'mä' },
	{ wert: 7, code: 'H7', bezeichnung: 'ziemlich stark zersetzt', pflanzenstruktur: 'kaum noch erkennbar', quetschen: 'ca. ½ des Torfes tritt durch', pflanzenreste: 'vorwiegend breiig, wenige erkennbare Reste', gruppe: 'mä' },
	{ wert: 8, code: 'H8', bezeichnung: 'sehr stark zersetzt', pflanzenstruktur: 'sehr undeutlich, fast nicht erkennbar', quetschen: 'ca. ⅔ des Torfes tritt durch', pflanzenreste: 'breiig, vereinzelte Wurzel-/Faserreste', gruppe: 'st' },
	{ wert: 9, code: 'H9', bezeichnung: 'fast vollständig zersetzt', pflanzenstruktur: 'nicht erkennbar', quetschen: 'fast alles quillt durch', pflanzenreste: 'ohne erkennbare Reste', gruppe: 'st' },
	{ wert: 10, code: 'H10', bezeichnung: 'völlig zersetzt', pflanzenstruktur: 'nicht erkennbar, völlig amorph', quetschen: 'gesamte Masse tritt durch', pflanzenreste: 'ohne erkennbare Reste', gruppe: 'st' },
];

/** Die Stufen für das Stufen-Feld (`wd-stufen-input`): Wert, Farbe der Gruppe, Name. */
export const ZERSETZUNGSGRAD_STUFEN_FELD: StufenBeschreibung[] = ZERSETZUNGSGRAD_STUFEN.map(s => ({
	wert: s.wert, farbe: ZERSETZUNGSGRAD_GRUPPEN[s.gruppe].farbe, name: s.bezeichnung,
}));

/** Eine Zeile der Tabelle im Hilfefenster; die Gruppen-Zelle spannt sich über ihre Stufen. */
export interface ZersetzungsgradZeile {
	stufe: string;
	bezeichnung: string;
	pflanzenstruktur: string;
	quetschen: string;
	pflanzenreste: string;
	gruppe: ZersetzungsGruppe;
	gruppenName: string;
	farbe: string;
	/** Nur an der ersten Zeile einer Gruppe: wie viele Zeilen die Gruppen-Zelle umfasst. */
	gruppenZeilen?: number;
	gruppenStart: boolean;
}

/** Die Tabelle des Hilfefensters aus derselben Reihe. */
export function zersetzungsgradZeilen(): ZersetzungsgradZeile[] {
	return ZERSETZUNGSGRAD_STUFEN.map((s, i) => {
		const start = i === 0 || ZERSETZUNGSGRAD_STUFEN[i - 1].gruppe !== s.gruppe;
		const gruppe = ZERSETZUNGSGRAD_GRUPPEN[s.gruppe];
		return {
			stufe: `${s.wert} (${s.code})`,
			bezeichnung: s.bezeichnung,
			pflanzenstruktur: s.pflanzenstruktur,
			quetschen: s.quetschen,
			pflanzenreste: s.pflanzenreste,
			gruppe: s.gruppe,
			gruppenName: gruppe.name,
			farbe: gruppe.farbe,
			gruppenZeilen: start ? ZERSETZUNGSGRAD_STUFEN.filter(x => x.gruppe === s.gruppe).length : undefined,
			gruppenStart: start,
		};
	});
}
